import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Ban, ClipboardList, MessageCircleQuestion, MessageSquare, Users } from 'lucide-react';
import { getCampaignStats, type Campaign, type HistoryEntry, type Segment } from '../../lib/supabase';
import { STAND_META, STAND_TEXT_REDAKTION, standVon } from '../../lib/freigabe';
import { vorlage } from '../../lib/mailBuilder/vorlagen';
import { formatDateTime, formatNumber, formatPercent, roleLabel } from '../../lib/format';
import { FreigabeBadge, Freigabelauf } from '../Freigabe';
import { MailVorschau } from '../MailVorschau/MailVorschau';
import './MailDetail.css';

type Props = {
  c: Campaign;
  verlauf: HistoryEntry[];
  verlaufLaedt: boolean;
  gruppen: Segment[];
  /** autor: die eigene Mail · redaktion: Prüfen vor der Freigabe */
  sicht: 'autor' | 'redaktion';
  aktionen?: ReactNode;
  tourAktionen?: string;
};

/** Eine Mail mit allem, was man zum Verstehen und Entscheiden braucht. */
export function MailDetail({ c, verlauf, verlaufLaedt, gruppen, sicht, aktionen, tourAktionen }: Props) {
  const v = vorlage(c.builder_template);
  const stand = standVon(c);
  const meta = STAND_META[stand];
  const ra = c.request_answers ?? null;
  const gewaehlt = gruppen.filter((g) => c.segment_ids?.includes(g.id));
  const fehlend = (c.segment_ids ?? []).filter((id) => !gruppen.some((g) => g.id === id)).length;
  const intern = gruppen.filter((g) => g.always_included);
  const summe = gewaehlt.reduce((n, g) => n + (g.contact_count ?? 0), 0);
  const nachricht = [...verlauf].reverse().find((h) => (h.action === 'eingereicht' || h.action === 'erneut_eingereicht') && h.message)?.message;
  const betreff = c.original_subject || c.subject || '(ohne Betreff)';

  const statistik = useQuery({
    queryKey: ['campaign-stats', c.id],
    queryFn: () => getCampaignStats(c.id),
    enabled: stand === 'versendet',
  });
  const st = statistik.data;

  return (
    <div className="md">
      <div className="md-kopf">
        <div className="md-badges">
          <FreigabeBadge campaign={c} />
          {c.is_urgent && <span className="badge badge-danger">Eilt</span>}
          {v && <span className="badge badge-neutral">{v.emoji} {v.name}</span>}
        </div>
        <h2 className="md-titel">{betreff}</h2>
        <p className="md-stand">
          <strong>{meta.wer !== '—' ? `Dran: ${meta.wer === 'Du' && sicht === 'redaktion' ? (c.created_by_name ?? 'die Person') : meta.wer}. ` : ''}</strong>
          {sicht === 'redaktion' ? (STAND_TEXT_REDAKTION[stand] ?? meta.text) : meta.text}
        </p>
      </div>

      {aktionen && <div className="md-aktionen" data-tour={tourAktionen}>{aktionen}</div>}

      {c.review_state === 'rueckfrage' && c.rejection_reason && (
        <div className="md-hinweis md-hinweis--frage">
          <MessageCircleQuestion size={17} aria-hidden="true" />
          <div><strong>Rückfrage der Redaktion{c.rejected_at ? ` (${formatDateTime(c.rejected_at)})` : ''}:</strong><p>{c.rejection_reason}</p></div>
        </div>
      )}
      {stand === 'abgelehnt' && c.rejection_reason && (
        <div className="md-hinweis md-hinweis--nein">
          <Ban size={17} aria-hidden="true" />
          <div><strong>Abgelehnt{c.rejected_at ? ` am ${formatDateTime(c.rejected_at)}` : ''}:</strong><p>{c.rejection_reason}</p></div>
        </div>
      )}
      {stand === 'fehler' && c.last_error && (
        <div className="md-hinweis md-hinweis--nein">
          <AlertTriangle size={17} aria-hidden="true" />
          <div><strong>Fehler beim Versand:</strong><p>{c.last_error}</p></div>
        </div>
      )}

      {st && (
        <div className="md-zahlen">
          <div><span>Zugestellt</span><strong>{formatNumber(st.delivered_count || st.sent_count)}</strong></div>
          <div><span>Geöffnet</span><strong>{formatPercent(st.unique_opens, st.delivered_count || st.sent_count)}</strong></div>
          <div><span>Geklickt</span><strong>{formatPercent(st.unique_clicks, st.delivered_count || st.sent_count)}</strong></div>
          <div><span>Abgemeldet</span><strong>{formatNumber(st.unsubscribed)}</strong></div>
        </div>
      )}

      <dl className="md-fakten">
        {sicht === 'redaktion' && (
          <div><dt>Von</dt><dd>{c.created_by_name ?? '—'}{c.created_by_role ? ` · ${roleLabel(c.created_by_role)}` : ''}{c.created_by_email ? ` · ${c.created_by_email}` : ''}</dd></div>
        )}
        <div><dt>Sportschule</dt><dd>{c.target_studios?.length ? c.target_studios.join(', ') : '—'}</dd></div>
        <div><dt>Wunschtermin</dt><dd>{c.desired_send_at ? formatDateTime(c.desired_send_at) : '—'}{c.is_urgent && c.urgent_reason ? <> · <strong>eilt:</strong> {c.urgent_reason}</> : ''}</dd></div>
        {c.submitted_for_approval_at && <div><dt>Eingereicht</dt><dd>{formatDateTime(c.submitted_for_approval_at)}</dd></div>}
        {c.scheduled_at && <div><dt>Geplant für</dt><dd>{formatDateTime(c.scheduled_at)}</dd></div>}
        {c.sent_at && <div><dt>Versendet</dt><dd>{formatDateTime(c.sent_at)}</dd></div>}
        <div><dt>Absender</dt><dd>{c.sender_name} &lt;{c.sender_email}&gt;</dd></div>
        <div><dt>Fragen an</dt><dd>{c.reply_to || '—'}</dd></div>
        {c.preview_text && <div><dt>Vorschautext</dt><dd>{c.preview_text}</dd></div>}
      </dl>

      <section className="md-abschnitt">
        <h3><Users size={15} aria-hidden="true" /> Empfänger</h3>
        {gewaehlt.length === 0 && fehlend === 0 ? <p className="md-leer">Noch keine Gruppe gewählt.</p> : (
          <ul className="md-liste">
            {gewaehlt.map((g) => <li key={g.id}><span>{g.name}</span><span>{formatNumber(g.contact_count)}</span></li>)}
            {fehlend > 0 && <li><span>{fehlend} weitere Gruppe{fehlend === 1 ? '' : 'n'} (für Dich nicht sichtbar)</span><span /></li>}
          </ul>
        )}
        {gewaehlt.length > 0 && <p className="md-klein">Bis zu {formatNumber(summe)} Postfächer — Doppelte zählen einmal, ohne Einwilligung bekommt niemand die Mail.</p>}
        {intern.length > 0 && <p className="md-klein">Immer in Kopie: {intern.map((g) => g.name).join(', ')}.</p>}
        {ra?.zielgruppe && <p className="md-klein"><strong>Wunsch zur Zielgruppe:</strong> {ra.zielgruppe}</p>}
      </section>

      {ra?.antworten && ra.antworten.length > 0 && (
        <section className="md-abschnitt" data-tour="freigaben-antworten">
          <h3><ClipboardList size={15} aria-hidden="true" /> Antworten auf die Vorab-Fragen</h3>
          <ul className="md-antworten">
            {ra.antworten.map((a) => (
              <li key={a.id}>
                <span className="md-frage">{a.frage}</span>
                <span className="md-antwort">{a.antwort}</span>
                {a.hinweis && <span className="md-antwort-hinweis"><AlertTriangle size={13} aria-hidden="true" /> {a.hinweis}</span>}
              </li>
            ))}
          </ul>
          {ra.offene_punkte && ra.offene_punkte.length > 0 && (
            <div className="md-hinweis md-hinweis--nein" style={{ marginTop: '0.75rem' }}>
              <AlertTriangle size={17} aria-hidden="true" />
              <div><strong>Noch offen:</strong><ul>{ra.offene_punkte.map((p) => <li key={p}>{p}</li>)}</ul></div>
            </div>
          )}
        </section>
      )}

      {sicht === 'redaktion' && nachricht && (
        <section className="md-abschnitt">
          <h3><MessageSquare size={15} aria-hidden="true" /> Nachricht an die Redaktion</h3>
          <p className="md-nachricht">{nachricht}</p>
        </section>
      )}

      <section className="md-abschnitt">
        <MailVorschau html={c.html_content || c.stripo_html || ''} titel="Vorschau" />
      </section>

      <Freigabelauf campaign={c} verlauf={verlauf} laedt={verlaufLaedt} sicht={sicht} />
    </div>
  );
}
